// src/pages/OTPVerification.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utilities/api';
import { isActivatedComplete } from '../contexts/UserContext';

const OTP_LENGTH = 6;

function OTPVerification({ onSuccess, onDataUpdate, setLoading, setMessage, setError, initialData }) {
  const [otp, setOtp] = useState('');
  const [currentCapability, setCurrentCapability] = useState(initialData?.capability || '');
  const verificationChannel = String(initialData?.verification_channel || initialData?.preferred_contact || 'EMAIL').toUpperCase();
  const currentDestination = verificationChannel === 'TEXT'
    ? initialData?.phone_mobile
    : initialData?.email;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

const LOCAL_KEY_PREFIX = 'redimdwm_';

  // Update local state if props change (e.g., initial load or parent update)
  useEffect(() => {

    // email is confirmed, then the token has been verified, if the token has been verified go to the password
    if (isActivatedComplete(initialData?.activated)) {
      onSuccess();
    }

    setCurrentCapability(initialData?.capability || '');
  }, [initialData?.activated, initialData?.capability, initialData?.email, initialData?.phone_mobile, initialData?.verification_channel, initialData?.preferred_contact]);

  // --- Submit Handler (Verify OTP) ---
   const handleSubmit = async (e) => {
    e.preventDefault();
    verifyOTP();
  };

  // --- Input Change Handler ---
  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setOtp(value.slice(0, OTP_LENGTH));
  };

  // --- Resend Handler ---
  const handleResend = async () => {
    setError('');
    setMessage('');
    setOtp('');

    if (!currentCapability) {
      setError('This verification session is missing or expired. Please restart registration.');
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('redimdwm_')) localStorage.removeItem(key);
        });
      navigate('/');
      return;
    }

    setIsResending(true);
    setLoading(true);

    try {
      const response = await api.resendOTP({
        capability: currentCapability,
        origin: window.location.origin,
      });

      if (response.success) {
        if (!response.capability) {
          setCurrentCapability('');
          onDataUpdate({ capability: null });
          setError('A new code was sent, but this browser could not continue verification. Please restart registration.');
          return;
        }

        setMessage('A new verification code has been sent.');
        setCurrentCapability(response.capability);
        onDataUpdate({ capability: response.capability });
      } else {
        setError(response.error || 'Unable to continue verification.');
      }
    } catch {
      setError('An error occurred while resending OTP. Please try again.');
    } finally {
      setIsResending(false);
      setLoading(false);
    }
  };

  async function verifyOTP() {
    setError('');
    setMessage('');
    setIsSubmitting(true);
    setLoading(true);

    if (!currentCapability) {
        setError('This verification session is missing or expired. Please restart registration.');
        setIsSubmitting(false);
        setLoading(false);
        return;
    }
    if (otp.length !== OTP_LENGTH) {
        setError(`Please enter the complete ${OTP_LENGTH}-digit OTP.`);
        setIsSubmitting(false);
        setLoading(false);
        return;
    }

    try {
      const response = await api.verifyOTP({
        capability: currentCapability,
        otp,
      });
      if (response.success && response.jwt) {
        const effectiveChannel = String(response.verification_channel || verificationChannel).toUpperCase();
        setCurrentCapability('');
        localStorage.removeItem(LOCAL_KEY_PREFIX+'token');
        onSuccess({
          userid: response.userid,
          user_name: response.user_name || initialData?.user_name || initialData?.email || '',
          email: response.email || initialData?.email || '',
          jwt: response.jwt,
          verification_channel: effectiveChannel,
          confirmed_email: response.confirmed_email || (effectiveChannel === 'EMAIL' ? '1' : '0'),
          confirmed_phone: response.confirmed_phone || (effectiveChannel === 'TEXT' ? '1' : '0'),
          capability: null,
          registration_step:"otp"
        });

      } else {
        if (response.success) {
          setCurrentCapability('');
          onDataUpdate({ capability: null });
        }
        setError(response.error || 'Unable to continue verification.');
      }
    } catch {
      setError('An error occurred during OTP verification. Please try again.');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }    
  }

  return (
    <div className="flex flex-col h-full">
        <h3 className="text-lg font-semibold text-primary mb-4">Verify Your Contact</h3>
        <p className="mb-4 text-sm text-neutral-dark">
            We’ve sent a {OTP_LENGTH}-digit code to {currentDestination || 'your selected contact method'}. Please enter it below.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4 flex-grow">
            <input
                type="text"
                inputMode="numeric"
                pattern={`\\d{${OTP_LENGTH}}`}
                placeholder={`Enter ${OTP_LENGTH}-digit OTP`}
                value={otp}
                onChange={handleChange}
                className="w-full text-center tracking-[0.3em] text-xl md:text-2xl font-semibold p-2 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={OTP_LENGTH}
                required
                disabled={isSubmitting || isResending}
                autoComplete="one-time-code"
            />

            <button
                type="submit"
                className={`w-full bg-button text-white py-2 rounded-md hover:bg-button-light transition duration-200 ${
                    (isSubmitting || isResending || otp.length !== OTP_LENGTH) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isSubmitting || isResending || otp.length !== OTP_LENGTH}
            >
                {isSubmitting ? 'Verifying...' : 'Verify & Continue'}
            </button>
            <button
                type="button"
                onClick={handleResend}
                className={`text-primary w-full hover:underline text-sm ${
                    (isSubmitting || isResending) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isSubmitting || isResending}
            >
                {isResending ? 'Resending...' : 'Resend OTP'}
            </button>            
        </form>
    </div>
  );
}

export default OTPVerification;
